/**
 * Workspace intelligence payloads for LexCounsel (Phase 2).
 * Describes where the lawyer is working — distinct from case/draft text context.
 */

export type WorkspaceScreen =
  | 'dashboard'
  | 'case'
  | 'draft_editor'
  | 'library'
  | 'templates'
  | 'calendar'
  | 'settings';

export interface WorkspaceEditorState {
  selectedText?: string;
  cursorPosition?: number | null;
  hasSelection?: boolean;
}

export interface WorkspaceDraftMetadata {
  documentType?: string;
  draftingStage?: string;
  templateOrigin?: 'recommendation' | 'scratch' | 'import' | 'template' | string;
  documentTitle?: string;
  workflowCommandId?: string;
  recommendationId?: string;
  documentId?: string;
}

export interface WorkspaceNavigation {
  route?: string;
  activeTabPath?: string;
}

export interface WorkspaceContextPayload {
  screen?: WorkspaceScreen;
  workspaceId?: string;
  caseId?: string;
  draftId?: string;
  templateId?: string;
  recommendationId?: string;
  activeTabId?: string;
  workflow?: string;
  editor?: WorkspaceEditorState;
  draftMetadata?: WorkspaceDraftMetadata;
  navigation?: WorkspaceNavigation;
}

export interface DraftEditorWorkspaceInput {
  workspaceId?: string;
  caseId?: string;
  draftId?: string;
  documentId?: string;
  documentTitle?: string;
  activeTabId?: string;
  isTemplateMode?: boolean;
  templateOrigin?: WorkspaceDraftMetadata['templateOrigin'];
  recommendationId?: string;
  workflowCommandId?: string;
  selectedText?: string;
  hasSelection?: boolean;
  route?: string;
  documentType?: string;
}

export interface CaseWorkspaceInput {
  workspaceId?: string;
  caseId?: string;
  route?: string;
  caseTitle?: string;
  nextHearingDate?: string | null;
}

export interface DashboardWorkspaceInput {
  workspaceId?: string;
  route?: string;
}

export interface LibraryWorkspaceInput {
  workspaceId?: string;
  route?: string;
  researchSessionId?: string;
}

/** Build workspace context for the draft editor / LexCounsel drafting surface. */
export function buildDraftEditorWorkspaceContext(
  input: DraftEditorWorkspaceInput,
): WorkspaceContextPayload {
  const hasSelection = Boolean(input.hasSelection || (input.selectedText || '').trim());
  return {
    screen: 'draft_editor',
    workspaceId: input.workspaceId,
    caseId: input.caseId,
    draftId: input.draftId || input.documentId,
    activeTabId: input.activeTabId,
    recommendationId: input.recommendationId,
    workflow: hasSelection ? 'improve_selection' : 'draft_document',
    editor: {
      selectedText: input.selectedText || '',
      hasSelection,
    },
    draftMetadata: {
      documentTitle: input.documentTitle,
      documentId: input.documentId,
      documentType: input.documentType,
      templateOrigin: input.isTemplateMode ? 'template' : input.templateOrigin,
      workflowCommandId: input.workflowCommandId,
      recommendationId: input.recommendationId,
    },
    navigation: input.route ? { route: input.route, activeTabPath: input.route } : undefined,
  };
}

/** Build workspace context for case-file LexCounsel Q&A. */
export function buildCaseWorkspaceContext(input: CaseWorkspaceInput): WorkspaceContextPayload {
  return {
    screen: 'case',
    workspaceId: input.workspaceId,
    caseId: input.caseId,
    workflow: 'case_qa',
    navigation: input.route ? { route: input.route, activeTabPath: input.route } : undefined,
  };
}

/** Build workspace context for dashboard LexCounsel. */
export function buildDashboardWorkspaceContext(input: DashboardWorkspaceInput): WorkspaceContextPayload {
  return {
    screen: 'dashboard',
    workspaceId: input.workspaceId,
    workflow: 'case_overview',
    navigation: input.route ? { route: input.route, activeTabPath: input.route } : undefined,
  };
}

/** Build workspace context for library / research. */
export function buildLibraryWorkspaceContext(input: LibraryWorkspaceInput): WorkspaceContextPayload {
  return {
    screen: 'library',
    workspaceId: input.workspaceId,
    workflow: 'research_statute',
    navigation: input.route ? { route: input.route, activeTabPath: input.route } : undefined,
  };
}

/** Infer screen from route path. */
export function inferScreenFromRoute(pathname: string): WorkspaceScreen {
  if (pathname.startsWith('/cases/')) return 'case';
  if (pathname.startsWith('/drafts/')) return 'draft_editor';
  if (pathname.startsWith('/library')) return 'library';
  if (pathname.startsWith('/diary') || pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname === '/dashboard' || pathname === '/') return 'dashboard';
  return 'dashboard';
}
